import { useEffect } from "react";

import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import Grid from "@mui/material/Grid";

import { CenteredLoading } from "../../../ui";
import { GeneralInformationSection } from "./general-information-section/general-information-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./inverted-standard-purchase-order-form-constants";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import { PurchaseOrderProductVariantSection } from "./purchase-order-product-variant-section";

export const InvertedStandardPurchaseOrderForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
}) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { reset, handleSubmit, formState } = methods;

  useEffect(() => {
    if (!defaultValues) {
      return;
    }

    reset({ ...DEFAULT_VALUES, ...defaultValues });
  }, [defaultValues, reset]);

  const onFormSubmit = async (formData) => {
    const payload = {
      taxable: formData.taxable,
      totalIncludesTax: formData.totalIncludesTax,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      sellerId: formData.seller.id,
      buyerableId: formData.buyer.id,
      paymentTermId: formData.paymentTerm.id,
      note: formData.note || null,
      taxAmount: formData.taxAmount,
      taxPercentage: formData.taxPercentage,
      discountType: formData.discountType,
      discountAmount: formData.discountInput || 0,
      grandTotal: formData.grandTotal,
      purchaseOrderProductVariants: formData.purchaseOrderProductVariants
        .filter((variant) => variant.isSelected && variant.quantity > 0)
        .map((variant) => ({
          sku: variant.sku,
          quantity: variant.quantity,
          discount: variant.discount || 0,
          costPerUnit: variant.costPerUnit || 0,
          note: variant.note || null,
        })),
    };

    await onSubmit(payload);
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <PurchaseOrderProductVariantSection />
        </Grid>
        <Grid size={{ xs: 12, md: 5, lg: 7 }}>
          <OtherSection />
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 5 }}>
          <PriceSummarySection />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

InvertedStandardPurchaseOrderForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
};
